import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  FormField,
  Input,
} from '@bookaburra/ui';
import { XCircle } from 'lucide-react';
import { authClient } from '../auth/client';
import { createTenant, TenantApiError } from '../booking-flow/tenant-api';
import { AppHeader } from '../components/AppHeader';
import { useDocumentTitle } from '../lib/use-document-title';
import { PENDING_PLAN_KEY } from './signup';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9 -]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const ERROR_COPY: Record<string, string> = {
  name_required: 'Pick a business name.',
  name_length: 'Business name must be between 2 and 80 characters.',
  invalid_slug: 'Slug must be lowercase letters, numbers and hyphens.',
  reserved_slug: 'That slug is reserved. Pick another.',
  slug_taken: 'That slug is already taken. Pick another.',
  not_authenticated: 'Please sign in first.',
};

function OnboardingPage() {
  useDocumentTitle('Welcome');
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  useEffect(() => {
    // If signed out, send to /signin with a redirect back here
    if (!isPending && !session?.user) {
      navigate({ to: '/signin', search: { redirect: '/onboarding' } });
    }
  }, [isPending, session?.user, navigate]);

  const mutation = useMutation({
    mutationFn: createTenant,
    onSuccess: (data) => {
      navigate({ to: '/admin/$slug', params: { slug: data.tenant.slug } });
    },
  });

  const errorCode =
    mutation.error instanceof TenantApiError ? mutation.error.code : null;
  const errorMessage = errorCode ? ERROR_COPY[errorCode] ?? errorCode : null;

  const canSubmit = useMemo(
    () => name.trim().length >= 2 && slug.length >= 2 && !mutation.isPending,
    [name, slug, mutation.isPending],
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    // Pick up the plan stashed by /signup. Server falls back to 'free'
    // if missing or unknown, so we don't need to validate client-side.
    let plan: string | undefined;
    try {
      plan = window.localStorage.getItem(PENDING_PLAN_KEY) ?? undefined;
    } catch {
      plan = undefined;
    }
    mutation.mutate(
      { name: name.trim(), slug, ...(plan ? { plan } : {}) },
      {
        onSuccess: () => {
          try {
            window.localStorage.removeItem(PENDING_PLAN_KEY);
          } catch {
            // best-effort cleanup
          }
        },
      },
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto flex max-w-md flex-col px-6 py-12 sm:py-16">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Step 1 of 1
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">
            Set up your business
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You&rsquo;ll be the owner of this tenant. You can invite staff after
            this.
          </p>
        </header>

      <form onSubmit={onSubmit} className="space-y-5 rounded-2xl border border-brand-sage/15 bg-card p-8 shadow-brand">
        <FormField label="Business name" required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              autoComplete="organization"
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sunshine Imaging"
            />
          )}
        </FormField>

        <FormField
          label="Slug"
          description="Used in your booking URL: bookaburra.com/<slug>. Lowercase letters, numbers, hyphens."
          required
        >
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              required
              invalid={invalid}
              aria-describedby={describedBy}
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
              placeholder="sunshine-imaging"
            />
          )}
        </FormField>

        {errorMessage ? (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Couldn&rsquo;t create the business</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          loading={mutation.isPending}
          disabled={!canSubmit}
        >
          Create
        </Button>

        <p className="text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">
            ← back home
          </Link>
        </p>
      </form>
      </main>
    </div>
  );
}
